import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import { FaRobot, FaShieldAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { PALETTE_PRESETS } from '../constants/colorPalettes';

const detectModules = (scanData = {}) => {
  const modules = [];
  if (scanData['Ad Scan']) modules.push('AD Scan');
  if (scanData.network_devices || scanData.localhosts?.some(m => m?.scan_results?.network_devices?.length)) modules.push('Network Scan');
  // `localhosts` is the multi-device envelope; `localhost` the older flat single machine.
  if (scanData.localhost || Array.isArray(scanData.localhosts)) modules.push('Localhost Scan');
  if (scanData.MS365) modules.push('MS365 Scan');
  if (scanData.PII) modules.push('PII Scan');
  if (scanData.external || scanData.categories || scanData.domain) modules.push('External Risk Scan');
  if (scanData.Onprem) modules.push('Infrastructure');
  return modules;
};

function AIReportBuilderModal({ show, onHide, scanFilePath }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState({
    enabled: false,
    model: 'gpt-5.4',
    loading: true,
    isTenantKey: false,
    promptsRemaining: undefined,
    promptLimit: undefined,
    quotaExceeded: false
  });
  const [templateName, setTemplateName] = useState('AI Generated Security Report');
  const [orientation, setOrientation] = useState('portrait');
  const [selectedPreset, setSelectedPreset] = useState('default');
  const [customPrimary, setCustomPrimary] = useState('#093070');
  const [customSecondary, setCustomSecondary] = useState('#6fca42');
  const [customAccent, setCustomAccent] = useState('#fdbc13');
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [stage, setStage] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!show) return;

    let cancelled = false;
    const loadStatus = async () => {
      setError('');
      setStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await apifetch('/s3/api/ai-report-builder/status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load AI status');
        if (!cancelled) {
          setStatus({
            enabled: !!data.enabled,
            model: data.model || 'gpt-5.4',
            isTenantKey: !!data.isTenantKey,
            promptsRemaining: data.promptsRemaining,
            promptLimit: data.promptLimit,
            quotaExceeded: !!data.quotaExceeded,
            loading: false
          });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({
            enabled: false,
            model: 'gpt-5.4',
            isTenantKey: false,
            promptsRemaining: undefined,
            promptLimit: undefined,
            quotaExceeded: false,
            loading: false
          });
          setError(err.message);
        }
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [show, token]);

  useEffect(() => {
    if (!show || !scanFilePath || !token) return;

    let cancelled = false;
    const loadModules = async () => {
      setLoadingModules(true);
      try {
        const response = await apifetch('/s3/api/report-metrics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ fileUrl: scanFilePath })
        });
        const data = await response.json().catch(() => ({}));
        if (!cancelled && response.ok) {
          setModules(detectModules(data.data || data.scanData || data || {}));
        }
      } catch (err) {
        if (!cancelled) setModules([]);
      } finally {
        if (!cancelled) setLoadingModules(false);
      }
    };

    loadModules();
    return () => {
      cancelled = true;
    };
  }, [show, scanFilePath, token]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setProgress(10);
    setStage('Reading scan data...');

    try {
      setProgress(35);
      setStage('Generating report template with AI...');
      const currentPresetData = PALETTE_PRESETS.find(p => p.id === selectedPreset) || PALETTE_PRESETS[0];
      const primaryColor = selectedPreset === 'custom' ? customPrimary : currentPresetData.primary;
      const secondaryColor = selectedPreset === 'custom' ? customSecondary : currentPresetData.secondary;
      const accentColor = selectedPreset === 'custom' ? customAccent : currentPresetData.accent;

      const response = await apifetch('/s3/api/ai-report-builder/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          fileUrl: scanFilePath,
          mode: 'template',
          save: true,
          templateName,
          orientation,
          selectedModules: modules,
          colorPalette: {
            name: currentPresetData.name,
            primary: primaryColor,
            secondary: secondaryColor,
            accent: accentColor
          }
        })
      });

      let data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start AI report generation');
      }

      if (data.jobId) {
        setProgress(40);
        setStage('AI is analyzing data and generating report (This may take 1-2 minutes)...');
        
        const jobId = data.jobId;
        let jobComplete = false;
        
        while (!jobComplete) {
            await new Promise(resolve => setTimeout(resolve, 4000)); // Poll every 4 seconds
            
            const statusRes = await apifetch(`/s3/api/ai-report-builder/job/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const statusData = await statusRes.json().catch(() => ({}));
            
            if (statusData.status === 'complete') {
                jobComplete = true;
                data = statusData; // Inherit the result
            } else if (statusData.status === 'error') {
                throw new Error(statusData.error || 'Failed to generate AI report template');
            } else if (!statusRes.ok) {
                throw new Error('Failed to check job status');
            }
            
            // Advance progress bar slowly to show activity
            setProgress(prev => Math.min(prev + 2, 80));
        }
      }

      setProgress(85);
      setStage('Opening editable report builder...');
      onHide?.();
      navigate('/report-builder?mode=template', {
        state: {
          // Use savedTemplateId returned from backend; fallback to templateId if present
          templateId: data.savedTemplateId || data.templateId,
          mode: 'template',
          fileUrl: scanFilePath,
          fromPastScans: true
        }
      });
    } catch (err) {
      setError(err.message);
      setProgress(0);
      setStage('');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal show={show} onHide={generating ? undefined : onHide} centered size="lg" enforceFocus={false}>
      <Modal.Header closeButton={!generating} className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <FaRobot className="me-2" />
          AI Report Builder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
        {error && <Alert variant="danger">{error}</Alert>}

        {status.loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-3 mb-0">Checking AI configuration...</p>
          </div>
        ) : !status.enabled ? (
          status.quotaExceeded ? (
            <Alert variant="danger">
              You have used all 5 trial prompts. A tenant admin must configure their own OpenAI API key under the Admin Panel -> Tenant tab to continue using the AI Report Builder.
            </Alert>
          ) : (
            <Alert variant="warning">
              AI Report Builder is not enabled. A tenant admin or platform admin must configure an OpenAI API key first.
            </Alert>
          )
        ) : (
          <>
            {!status.isTenantKey && status.promptsRemaining !== undefined && (
              <Alert variant="warning" className="d-flex align-items-center gap-2">
                <div>
                  <strong>Free Trial Mode:</strong> Using the system OpenAI key. You have <strong>{status.promptsRemaining} of {status.promptLimit}</strong> remaining prompts. Please add your own API key under Admin Panel -> Tenant tab for unlimited generation.
                </div>
              </Alert>
            )}

            <Alert variant="info" className="d-flex align-items-start gap-2">
              <FaShieldAlt className="mt-1" />
              <div>
                The full scan JSON will be sent to OpenAI to build an editable reusable report template. The API key stays on the server and is never exposed to your browser.
              </div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>Template Name</Form.Label>
              <Form.Control
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                disabled={generating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Orientation</Form.Label>
              <Form.Select value={orientation} onChange={(e) => setOrientation(e.target.value)} disabled={generating}>
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Color Palette Preset</Form.Label>
              <Form.Select 
                value={selectedPreset} 
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedPreset(val);
                  if (val !== 'custom') {
                    const preset = PALETTE_PRESETS.find(p => p.id === val);
                    if (preset) {
                      setCustomPrimary(preset.primary);
                      setCustomSecondary(preset.secondary);
                      setCustomAccent(preset.accent);
                    }
                  }
                }} 
                disabled={generating}
              >
                {PALETTE_PRESETS.map(preset => (
                  <option key={preset.id} value={preset.id}>{preset.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="mb-3 p-3 border rounded" style={{ borderColor: isDarkMode ? '#475569' : '#e2e8f0', backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc' }}>
              <div className="fw-semibold mb-2 small text-muted">Customize 3-Way Colors</div>
              <div className="row g-3">
                <div className="col-4">
                  <div className="d-flex flex-column gap-1">
                    <span className="small text-muted">Primary Color</span>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={customPrimary}
                        onChange={(e) => {
                          setCustomPrimary(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        title="Choose primary color"
                        style={{ width: '40px', height: '38px', padding: '2px', cursor: 'pointer' }}
                      />
                      <Form.Control
                        type="text"
                        value={customPrimary}
                        onChange={(e) => {
                          setCustomPrimary(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        maxLength={7}
                        className="form-control-sm text-monospace"
                        style={{ fontSize: '13px', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="col-4">
                  <div className="d-flex flex-column gap-1">
                    <span className="small text-muted">Secondary Color</span>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={customSecondary}
                        onChange={(e) => {
                          setCustomSecondary(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        title="Choose secondary color"
                        style={{ width: '40px', height: '38px', padding: '2px', cursor: 'pointer' }}
                      />
                      <Form.Control
                        type="text"
                        value={customSecondary}
                        onChange={(e) => {
                          setCustomSecondary(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        maxLength={7}
                        className="form-control-sm text-monospace"
                        style={{ fontSize: '13px', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-4">
                  <div className="d-flex flex-column gap-1">
                    <span className="small text-muted">Accent Color</span>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={customAccent}
                        onChange={(e) => {
                          setCustomAccent(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        title="Choose accent color"
                        style={{ width: '40px', height: '38px', padding: '2px', cursor: 'pointer' }}
                      />
                      <Form.Control
                        type="text"
                        value={customAccent}
                        onChange={(e) => {
                          setCustomAccent(e.target.value);
                          setSelectedPreset('custom');
                        }}
                        disabled={generating}
                        maxLength={7}
                        className="form-control-sm text-monospace"
                        style={{ fontSize: '13px', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="fw-semibold mb-2">AI Model</div>
              <Badge bg="secondary">{status.model}</Badge>
            </div>

            <div className="mb-3">
              <div className="fw-semibold mb-2">Detected Scan Modules</div>
              {loadingModules ? (
                <span className="text-muted">Detecting modules...</span>
              ) : modules.length ? (
                <div className="d-flex gap-2 flex-wrap">
                  {modules.map(module => <Badge bg="primary" key={module}>{module}</Badge>)}
                </div>
              ) : (
                <span className="text-muted">Modules will be detected during generation.</span>
              )}
            </div>

            {generating && (
              <div className="mt-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>{stage}</span>
                  <span>{progress}%</span>
                </div>
                <ProgressBar now={progress} animated />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Button variant="secondary" onClick={onHide} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={!status.enabled || generating || !templateName.trim() || !scanFilePath}
        >
          {generating ? <Spinner animation="border" size="sm" className="me-2" /> : <FaRobot className="me-2" />}
          Generate AI Template
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AIReportBuilderModal;
